import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Plus,
  RefreshCw,
  Eye,
  Edit,
  MoreHorizontal,
  ClipboardList,
  ArrowRight,
  Send,
  FileSearch,
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

// ─── Status pill ─────────────────────────────────────────────────────────────

const getStatusPill = (status) => {
  const config = {
    DRAFT: {
      bg: '#f3f4f6', color: '#6b7280', dot: '#6b7280', label: 'Draft',
    },
    SUBMITTED: {
      bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6', label: 'Submitted',
    },
    EVALUATED: {
      bg: '#ede9fe', color: '#5b21b6', dot: '#7c3aed', label: 'Evaluated',
    },
    APPROVED: {
      bg: '#d1fae5', color: '#059669', dot: '#10b981', label: 'Approved',
    },
    AWARDED: {
      bg: '#d1fae5', color: '#059669', dot: '#10b981', label: 'Awarded',
    },
    REJECTED: {
      bg: '#fee2e2', color: '#dc2626', dot: '#ef4444', label: 'Rejected',
    },
  };
  const s = config[status] || config.DRAFT;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontSize: 12,
      fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}22`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
};

// ─── Action dropdown ──────────────────────────────────────────────────────────

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      width: '100%', padding: '8px 14px', fontSize: 13,
      border: 'none', background: 'none', cursor: 'pointer',
      color: danger ? '#dc2626' : '#374151',
      transition: 'background 0.12s ease',
      textAlign: 'left',
    }}
    onMouseEnter={e => e.currentTarget.style.background = danger ? '#fef2f2' : '#f9fafb'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >
    <span style={{ color: danger ? '#ef4444' : '#9ca3af', flexShrink: 0 }}>{icon}</span>
    {label}
  </button>
);

const ResponseActionDropdown = ({ response, onView, onEdit }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef   = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', close);
      window.addEventListener('scroll', close, true);
    }
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const rect = buttonRef.current?.getBoundingClientRect();
  const isDraft = response.status === 'DRAFT';

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(p => !p)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 8,
          border: '1px solid #e5e7eb', cursor: 'pointer',
          background: open ? '#f3f4f6' : '#fff',
          transition: 'all 0.15s ease',
        }}
      >
        <MoreHorizontal size={16} style={{ color: open ? '#2563eb' : '#6b7280' }} />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: rect.bottom + 6,
            left: rect.left - 152,
            zIndex: 9999,
            width: 180,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '4px 0',
            animation: 'fadeIn 0.1s ease',
          }}
        >
          <MenuItem
            icon={<Eye size={15} />}
            label="View Submission"
            onClick={() => { setOpen(false); onView(response); }}
          />
          {isDraft && (
            <MenuItem
              icon={<Edit size={15} />}
              label="Edit Draft"
              onClick={() => { setOpen(false); onEdit(response); }}
            />
          )}
          {!isDraft && (
            <div style={{
              padding: '6px 14px',
              fontSize: 10, fontWeight: 700,
              color: '#9ca3af', textTransform: 'uppercase',
              letterSpacing: '0.06em',
              borderTop: '1px solid #f3f4f6', marginTop: 4,
            }}>
              Submission Locked
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const extractArray = (payload) => {
  if (Array.isArray(payload))            return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items))      return payload.items;
  if (Array.isArray(payload?.data))       return payload.data;
  return [];
};

const fmtDate = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

// ─── Main Component ───────────────────────────────────────────────────────────

const VendorResponseManagement = ({ isNested = false, rfpId: propRfpId }) => {
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();
  const showErrorRef     = useRef(showError);
  const loadRequestIdRef = useRef(0);

  const [responses,     setResponses]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [availableRfps, setAvailableRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState(propRfpId || '');

  useEffect(() => { showErrorRef.current = showError; }, [showError]);
  useEffect(() => { setSelectedRfpId(propRfpId || ''); }, [propRfpId]);

  const loadData = React.useCallback(async (rfpFilterId) => {
    const reqId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const filterId =
        typeof rfpFilterId === 'string'
          ? rfpFilterId
          : isNested ? (propRfpId || '') : (selectedRfpId || '');

      const [responsesRes, rfpsRes] = await Promise.all([
        SourcingRfpService.getResponses(filterId || ''),
        SourcingRfpService.getRfps(),
      ]);

      if (reqId !== loadRequestIdRef.current) return;
      setResponses(extractArray(responsesRes));
      setAvailableRfps(extractArray(rfpsRes));
    } catch (err) {
      if (reqId !== loadRequestIdRef.current) return;
      console.error('Vendor response load error:', err);
      setResponses([]);
      setAvailableRfps([]);
      showErrorRef.current('Could not connect to the sourcing service.');
    } finally {
      if (reqId === loadRequestIdRef.current) setLoading(false);
    }
  }, [isNested, propRfpId, selectedRfpId]);

  useEffect(() => {
    loadData(isNested ? (propRfpId || '') : (selectedRfpId || ''));
  }, [isNested, propRfpId, selectedRfpId, loadData]);

  // ─── Columns ─────────────────────────────────────────────────────────────
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '12px',
      sortable: false,
      render: () => <div style={{ width: 12 }} />,
    },
    {
      key: 'rfpInfo',
      label: 'RFP Details',
      render: (_, res) => (
        <div className="flex flex-col">
          <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
            {res.rfpId?.title || 'RFP Title Missing'}
          </span>
          <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {res.rfpId?.rfpNumber || 'No RFP Number'}
          </span>
        </div>
      ),
    },
    {
      key: 'bidAmount',
      label: 'Total Bid Value',
      render: (_, res) => {
        const total = (res.lineItems || []).reduce((s, i) => s + (i.price || 0), 0);
        return (
          <span style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>
            {total > 0 ? `$${total.toLocaleString()}` : '—'}
          </span>
        );
      },
    },
    {
      key: 'itemCount',
      label: 'Line Items',
      render: (_, res) => (
        <div className="flex items-center gap-1.5 text-gray-500" style={{ fontSize: 13 }}>
          <ClipboardList size={14} />
          {res.lineItems?.length || 0} items
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, res) => getStatusPill(res.status),
    },
    {
      key: 'updatedAt',
      label: 'Last Updated',
      render: (_, res) => (
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {fmtDate(res.updatedAt || res.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '70px',
      render: (_, res) => (
        <ResponseActionDropdown
          response={res}
          onView={(r) => navigate(`/sourcing/responses/view/${r._id}`)}
          onEdit={(r) => navigate(`/sourcing/responses/edit/${r._id}`)}
        />
      ),
    },
  ];

  // ─── KPI stats ────────────────────────────────────────────────────────────
  const stats = {
    total:     responses.length,
    draft:     responses.filter(r => r.status === 'DRAFT').length,
    submitted: responses.filter(r => r.status === 'SUBMITTED' || r.status === 'EVALUATED').length,
    approved:  responses.filter(r => r.status === 'APPROVED'  || r.status === 'AWARDED').length,
    rejected:  responses.filter(r => r.status === 'REJECTED').length,
  };

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
      variant: 'outline',
      onClick: () => loadData(),
    },
    {
      label: 'Browse RFPs',
      icon: <FileSearch size={18} />,
      variant: 'outline',
      onClick: () => navigate('/sourcing/available-rfps'),
    },
    {
      label: 'Add Response',
      icon: <Plus size={18} />,
      variant: 'primary',
      disabled: !selectedRfpId,
      onClick: () => {
        if (selectedRfpId) {
          navigate(`/sourcing/responses/add/${selectedRfpId}`);
        } else {
          showError('Please select an RFP from the dropdown first, or browse available RFPs.');
        }
      },
    },
  ];

  return (
    <>
      {!isNested && <PageBreadcrumb />}
      {!isNested && (
        <PageHeader
          title="My Responses"
          description="Track and manage your proposals for open sourcing events"
          actions={headerActions}
        />
      )}

      <div className={isNested ? '' : 'page-container'}>

        {/* ── Browse CTA Banner ── */}
        {!isNested && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            border: '1px solid #bfdbfe', borderRadius: 12,
            padding: '14px 20px', marginBottom: 20,
          }}>
            <div>
              <p style={{ fontWeight: 700, color: '#1e40af', margin: 0, fontSize: 14 }}>
                Looking for new opportunities?
              </p>
              <p style={{ color: '#3b82f6', margin: '2px 0 0', fontSize: 13 }}>
                Browse all published RFPs and submit a new proposal.
              </p>
            </div>
            <button
              onClick={() => navigate('/sourcing/available-rfps')}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 18px', borderRadius: 8,
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: '#fff', fontWeight: 600, fontSize: 13,
                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                flexShrink: 0,
              }}
            >
              <Send size={14} /> Browse Available RFPs <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* ── RFP Selector (non-nested) ── */}
        {(!isNested || !propRfpId) && (
          <div style={{
            display: 'flex', gap: 12, alignItems: 'center',
            background: '#f9fafb', padding: '14px 16px',
            borderRadius: 10, border: '1px solid #e5e7eb',
            marginBottom: 20,
          }}>
            <label style={{ fontWeight: 600, color: '#374151', fontSize: 13, whiteSpace: 'nowrap' }}>
              Filter by RFP:
            </label>
            <select
              value={selectedRfpId}
              onChange={e => setSelectedRfpId(e.target.value)}
              style={{
                flex: 1, maxWidth: 380, padding: '8px 12px',
                borderRadius: 8, border: '1px solid #d1d5db',
                fontSize: 13, color: '#111827', outline: 'none',
                background: '#fff',
              }}
            >
              <option value="">All My Responses</option>
              {availableRfps.map(rfp => (
                <option key={rfp._id} value={rfp._id}>
                  {rfp.rfpNumber} • {rfp.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── KPI Cards ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 16, marginBottom: 24,
        }}>
          {[
            { label: 'Total Proposals', value: stats.total,     color: '#111827' },
            { label: 'Drafts',          value: stats.draft,     color: '#d97706' },
            { label: 'Submitted',       value: stats.submitted, color: '#2563eb' },
            { label: 'Approved',        value: stats.approved,  color: '#059669' },
            { label: 'Rejected',        value: stats.rejected,  color: '#dc2626' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: '#fff', padding: '16px 18px',
              borderRadius: 10, border: '1px solid #e5e7eb',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Table ── */}
        <div style={{
          background: '#fff', borderRadius: 12,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}>
          <DataTable
            data={responses}
            columns={columns}
            loading={loading}
            showActions={false}
            emptyMessage={
              selectedRfpId
                ? 'No responses for this RFP yet. Click "Add Response" to start.'
                : 'No proposals yet. Browse available RFPs to submit your first bid.'
            }
          />
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default VendorResponseManagement;